import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useCarouselVideo Hook
 *
 * Manages video playback state for carousel slides.
 * Handles autoplay, loading states, and cleanup for multiple videos.
 *
 * @param totalSlides - Total number of slides in the carousel
 * @param isOpen - Whether the carousel modal is open
 * @param currentSlide - Index of the currently active slide
 *
 * @returns Video refs, loading states, and handlers
 */
export const useCarouselVideo = (totalSlides: number, isOpen: boolean, currentSlide: number) => {
  const [loadingStates, setLoadingStates] = useState<Map<number, boolean>>(
    new Map(Array.from({ length: totalSlides }, (_, i) => [i, true]))
  );
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Handle video loaded event
  const handleVideoLoaded = useCallback((index: number) => {
    setLoadingStates((prev) => {
      const newStates = new Map(prev);
      newStates.set(index, false);
      return newStates;
    });
  }, []);

  // Get video ref callback for a specific index
  const getVideoRef = useCallback((index: number) => {
    return (el: HTMLVideoElement | null) => {
      videoRefs.current[index] = el;
    };
  }, []);

  // Check if a video is loading
  const isVideoLoading = useCallback(
    (index: number) => {
      return loadingStates.get(index) ?? true;
    },
    [loadingStates]
  );

  // Play video for current slide
  const playCurrentVideo = useCallback(() => {
    const video = videoRefs.current[currentSlide];
    if (video) {
      video.currentTime = 0;
      video.play().catch((error) => {
        console.log('Carousel video autoplay prevented:', error);
      });
    }
  }, [currentSlide]);

  // Pause all videos except current
  const pauseOtherVideos = useCallback((exceptIndex: number) => {
    videoRefs.current.forEach((video, idx) => {
      if (video && idx !== exceptIndex) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, []);

  // Reset all videos
  const resetAllVideos = useCallback(() => {
    videoRefs.current.forEach((video) => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, []);

  // Auto-play video when slide changes
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      pauseOtherVideos(currentSlide);
      playCurrentVideo();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentSlide, isOpen, pauseOtherVideos, playCurrentVideo]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setLoadingStates(new Map(Array.from({ length: totalSlides }, (_, i) => [i, true])));
    } else {
      resetAllVideos();
    }
  }, [isOpen, totalSlides, resetAllVideos]);

  return {
    videoRefs,
    getVideoRef,
    isVideoLoading,
    handleVideoLoaded,
  };
};
